import { createContext, type ReactNode, useCallback, useContext, useMemo } from 'react';

import type { User } from '@/features/auth';
import { useCurrentUser } from '@/features/auth';

type AuthState = {
  user: User | null;
  isLoading: boolean;
  refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const me = useCurrentUser();
  const refresh = useCallback(async () => {
    await me.refetch();
  }, [me]);

  const value = useMemo<AuthState>(
    () => ({
      user: me.data ?? null,
      isLoading: me.isLoading,
      refresh,
    }),
    [me.data, me.isLoading, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
